/**
 * Global keyboard shortcuts.
 *
 *   esc       — close topmost drawer/modal/popover (drawer stack)
 *   cmd+s     — save active drawer's form (and prevent browser save dialog)
 *   j / k     — move row focus down/up on list pages
 *   enter     — open drawer for the focused row
 *   cmd/ctrl+k — open the command palette (useCommandPaletteHotkey; the
 *               palette itself is components/CommandPalette.tsx, mounted
 *               once in App.tsx so it works on every page, ops or not)
 *
 * Composition:
 *  - <KeyboardShortcutProvider> wraps the app; it owns the drawer stack +
 *    list-page registry.
 *  - <Drawer> calls `useDrawer(open, onClose, onSave?)` so it joins the
 *    stack (esc + cmd+s know what's active).
 *  - List pages call `useListShortcuts({ rows, onOpenDrawer })` so j/k/enter
 *    operate on the page's visible rows.
 */
import {
  type ReactNode, createContext, useCallback, useContext, useEffect,
  useMemo, useRef, useState,
} from 'react'
import { isCommandPaletteHotkey } from '../../lib/commandPalette'
import { escapeKeyAction } from './escapeLayer'

// ---------------------------------------------------------------------------
// Drawer stack
// ---------------------------------------------------------------------------
interface DrawerEntry {
  id: number
  onClose: () => void
  /** Optional save-handler invoked on cmd+s while this drawer is topmost. */
  onSave?: () => void
}

interface KbCtx {
  pushDrawer: (e: Omit<DrawerEntry, 'id'>) => number
  popDrawer: (id: number) => void
  /** Mutates the existing entry — used when handlers change inside a drawer. */
  updateDrawer: (id: number, patch: Partial<Omit<DrawerEntry, 'id'>>) => void
  /** Register a list-page focus handler. Returns an unregister fn. */
  registerList: (h: ListShortcutHandlers) => () => void
}

interface ListShortcutHandlers {
  /** Move focus by `delta` (+1 = down, -1 = up). */
  move: (delta: number) => void
  /** Activate currently-focused row (open drawer). */
  activate: () => void
}

const Ctx = createContext<KbCtx | null>(null)

export function KeyboardShortcutProvider({ children }: { children: ReactNode }) {
  const stackRef = useRef<DrawerEntry[]>([])
  const listRef = useRef<ListShortcutHandlers | null>(null)
  const nextId = useRef(1)
  // Bump to force consumers (like the help dropdown) to re-read if they
  // ever care about stack state. The global key handler reads stackRef
  // directly so it doesn't need this.
  const [, forceTick] = useState(0)

  const pushDrawer = useCallback((e: Omit<DrawerEntry, 'id'>) => {
    const id = nextId.current++
    stackRef.current = [...stackRef.current, { id, ...e }]
    forceTick((x) => x + 1)
    return id
  }, [])

  const popDrawer = useCallback((id: number) => {
    stackRef.current = stackRef.current.filter((d) => d.id !== id)
    forceTick((x) => x + 1)
  }, [])

  const updateDrawer = useCallback((id: number, patch: Partial<Omit<DrawerEntry, 'id'>>) => {
    stackRef.current = stackRef.current.map((d) => (d.id === id ? { ...d, ...patch } : d))
  }, [])

  const registerList = useCallback((h: ListShortcutHandlers) => {
    listRef.current = h
    return () => {
      if (listRef.current === h) listRef.current = null
    }
  }, [])

  // Global keydown listener — one for the whole app.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      const inEditable =
        !!target && (
          target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable
        )

      // esc — close topmost drawer/modal, even if focus is in an input (so
      // esc out of an inline edit doesn't leave you stuck). PR 10 review:
      // NOT when a layer above already used it (an overlay-kit drawer such as
      // the product quick view, open over this drawer, handles Escape on
      // document first and marks it) — one Escape closes one layer.
      const esc = escapeKeyAction(e)
      if (esc) {
        const top = stackRef.current[stackRef.current.length - 1]
        if (esc === 'close' && top) {
          e.preventDefault()
          top.onClose()
        }
        return
      }

      // cmd+s / ctrl+s — save topmost drawer with onSave; prevent browser save.
      if ((e.metaKey || e.ctrlKey) && (e.key === 's' || e.key === 'S')) {
        const top = stackRef.current[stackRef.current.length - 1]
        if (top?.onSave) {
          e.preventDefault()
          top.onSave()
        }
        return
      }

      // j/k/enter — only when no drawer is open AND focus isn't in an input.
      // Never with Ctrl/Cmd held: Ctrl+K is the command palette, not "up".
      if (stackRef.current.length > 0) return
      if (inEditable) return
      if (e.metaKey || e.ctrlKey || e.altKey) return

      if (e.key === 'j') {
        if (listRef.current) {
          e.preventDefault()
          listRef.current.move(1)
        }
        return
      }
      if (e.key === 'k') {
        if (listRef.current) {
          e.preventDefault()
          listRef.current.move(-1)
        }
        return
      }
      if (e.key === 'Enter') {
        if (listRef.current) {
          e.preventDefault()
          listRef.current.activate()
        }
        return
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const value = useMemo<KbCtx>(() => ({
    pushDrawer, popDrawer, updateDrawer, registerList,
  }), [pushDrawer, popDrawer, updateDrawer, registerList])

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Drawer hook — call from inside <Drawer> when it opens. Registers with
 * the stack so esc closes the topmost drawer, and cmd+s triggers save.
 *
 * `onClose` should be stable. `onSave` may change frequently — we update
 * the entry in-place rather than re-pushing.
 */
export function useDrawer(open: boolean, onClose: () => void, onSave?: () => void) {
  const ctx = useContext(Ctx)
  const idRef = useRef<number | null>(null)

  // Push on open / pop on close.
  useEffect(() => {
    if (!ctx) return
    if (!open) return
    const id = ctx.pushDrawer({ onClose, onSave })
    idRef.current = id
    return () => {
      ctx.popDrawer(id)
      idRef.current = null
    }
    // Only re-run when open toggles or onClose identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx, open, onClose])

  // Update the onSave handler in place when it changes.
  useEffect(() => {
    if (!ctx) return
    if (idRef.current == null) return
    ctx.updateDrawer(idRef.current, { onSave })
  }, [ctx, onSave])
}

/**
 * List-page hook — register handlers for j/k/enter. Returns the currently
 * focused index so the page can highlight the row.
 *
 *   const { focusedIndex } = useListShortcuts({
 *     rows: items,
 *     onOpenDrawer: (row) => setActive(row),
 *   })
 *
 * `enabled` (default true) gates registration. The global registry holds a
 * SINGLE active list handler (last-wins), so a page that embeds another
 * list-page as a sub-tab must NOT register while the sub-tab owns its own
 * list — otherwise the two fight over j/k. Pass `enabled: false` on the
 * parent when a sub-tab is active so only the visible list handles the keys.
 */
export function useListShortcuts<T>({
  rows, onOpenDrawer, enabled = true,
}: {
  rows: T[]
  onOpenDrawer: (row: T) => void
  enabled?: boolean
}): { focusedIndex: number } {
  const ctx = useContext(Ctx)
  const [focusedIndex, setFocusedIndex] = useState<number>(-1)
  const rowsRef = useRef<T[]>(rows)
  rowsRef.current = rows

  // Keep index in range as rows change.
  useEffect(() => {
    if (focusedIndex >= rows.length) setFocusedIndex(rows.length - 1)
  }, [rows.length, focusedIndex])

  // Ref-tracked focused index so the activate closure picks up the latest
  // value without re-registering on every move.
  const focusedIndexRef = useRef(focusedIndex)
  focusedIndexRef.current = focusedIndex

  useEffect(() => {
    if (!ctx) return
    // Disabled (e.g. a sibling sub-tab owns the visible list) → don't claim
    // the single global list slot. The returned focusedIndex is forced to -1
    // below so any stale row highlight disappears while this list is hidden.
    if (!enabled) return
    const unregister = ctx.registerList({
      move: (delta) => {
        setFocusedIndex((prev) => {
          const total = rowsRef.current.length
          if (total === 0) return -1
          const next = prev < 0 ? 0 : prev + delta
          if (next < 0) return 0
          if (next >= total) return total - 1
          return next
        })
      },
      activate: () => {
        const idx = focusedIndexRef.current
        if (idx < 0) return
        const row = rowsRef.current[idx]
        if (row != null) onOpenDrawer(row)
      },
    })
    return unregister
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx, onOpenDrawer, enabled])

  // When disabled, this list isn't visible — never report a focused row.
  return { focusedIndex: enabled ? focusedIndex : -1 }
}

/**
 * Command palette hotkey — Ctrl+K (Cmd+K on a Mac) calls `onOpen` from
 * anywhere, including while typing in an input (like every app's palette).
 * The browser's own Ctrl+K (focus the address-bar search) is suppressed.
 */
// eslint-disable-next-line react-refresh/only-export-components -- hooks live beside the provider they extend (same as useDrawer / useListShortcuts)
export function useCommandPaletteHotkey(onOpen: () => void) {
  const onOpenRef = useRef(onOpen)
  useEffect(() => { onOpenRef.current = onOpen }, [onOpen])
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!isCommandPaletteHotkey(e)) return
      e.preventDefault()
      onOpenRef.current()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])
}

/**
 * Keyboard help dropdown — a small `?` icon in page headers that shows the
 * shortcut list when clicked. Useful for discoverability.
 */
export function KeyboardHelpDropdown() {
  const [open, setOpen] = useState(false)
  const wrap = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  return (
    <div className="relative" ref={wrap}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="Keyboard shortcuts"
        aria-label="Keyboard shortcuts"
        className={`h-8 w-8 inline-flex items-center justify-center rounded-md border text-sm font-semibold
                    focus:outline-none focus:ring-2 focus:ring-violet-400 focus:ring-offset-1 ${
          open ? 'border-violet-400 bg-violet-50 text-violet-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
        }`}
      >
        ?
      </button>
      {open && (
        <div className="absolute right-0 mt-1 z-30 w-72 bg-white border border-gray-200 rounded-md shadow-lg p-3 text-sm">
          <h3 className="font-semibold text-gray-900 mb-2">Keyboard shortcuts</h3>
          <ul className="space-y-1.5">
            <ShortcutRow keys={['j']} label="Move focus down" />
            <ShortcutRow keys={['k']} label="Move focus up" />
            <ShortcutRow keys={['Enter']} label="Open focused row" />
            <ShortcutRow keys={['Esc']} label="Close drawer / modal" />
            <ShortcutRow keys={['⌘', 'S']} label="Save active form" />
            <ShortcutRow keys={['Ctrl', 'K']} label="Search pages and records" />
          </ul>
          <p className="mt-2 text-xs text-gray-500 leading-snug">
            j/k/enter work when no drawer is open and no input is focused.
          </p>
        </div>
      )}
    </div>
  )
}

function ShortcutRow({ keys, label }: { keys: string[]; label: string }) {
  return (
    <li className="flex items-center justify-between gap-3">
      <span className="text-gray-700">{label}</span>
      <span className="flex items-center gap-1">
        {keys.map((k, i) => (
          <kbd key={i}
               className="px-1.5 py-0.5 text-xs font-mono bg-gray-100 border border-gray-300 rounded text-gray-700">
            {k}
          </kbd>
        ))}
      </span>
    </li>
  )
}
